<template>
    <div class="apikey-manage-wrap">
      <!-- 查询区域 -->
      <div class="search-card">
        <el-form :model="queryForm" inline>
          <el-form-item label="机构名称">
            <el-input
              v-model="queryForm.companyName"
              placeholder="请输入机构名称"
              clearable
              @keyup.enter="getApikeyData"
            />
          </el-form-item>
          <el-form-item>
            <el-button type="primary" @click="getApikeyData">查询</el-button>
            <el-button @click="handleReset">重置</el-button>
          </el-form-item>
        </el-form>
      </div>
  
      <!-- 表格区域 -->
      <div class="table-card">
        <el-table
          :data="tableData"
          border
          stripe
          v-loading="loading"
          style="width:100%"
        >
          <el-table-column label="ID" prop="id" align="center" width="80" />
          <el-table-column label="机构ID" prop="company_id" align="center" width="120" />
          <el-table-column label="机构名称" prop="company_name" align="center" min-width="240" />
          <el-table-column label="ApiKey" align="center" min-width="340">
            <template #default="scope">
              <div class="key-text">
                <span v-if="!scope.row.showApi">{{ maskStr(scope.row.apiKey) }}</span>
                <span v-else>{{ scope.row.apiKey }}</span>
              </div>
              <div class="key-op">
                <el-button link size="small" @click="scope.row.showApi = !scope.row.showApi">
                  {{ scope.row.showApi ? "隐藏" : "显示" }}
                </el-button>
                <el-button link size="small" @click="copyText(scope.row.apiKey)">复制</el-button>
              </div>
            </template>
          </el-table-column>
          <el-table-column label="SecretKey" align="center" min-width="260">
            <template #default="scope">
              <div class="key-text">
                <span v-if="!scope.row.showSecret">{{ maskStr(scope.row.secretKey) }}</span>
                <span v-else>{{ scope.row.secretKey }}</span>
              </div>
              <div class="key-op">
                <el-button link size="small" @click="scope.row.showSecret = !scope.row.showSecret">
                  {{ scope.row.showSecret ? "隐藏" : "显示" }}
                </el-button>
                <el-button link size="small" @click="copyText(scope.row.secretKey)">复制</el-button>
              </div>
            </template>
          </el-table-column>
          <el-table-column label="操作" align="center" width="280">
            <template #default="scope">
              <el-button type="success" link @click="handleCreateKey(scope.row)">生成ApiKey</el-button>
              <el-button type="warning" link @click="handleResetKey(scope.row)">重置密钥</el-button>
              <el-button type="danger" link @click="handleDelete(scope.row)">删除</el-button>
            </template>
          </el-table-column>
        </el-table>
  
        <el-pagination
          v-model:current-page="pagination.page"
          v-model:page-size="pagination.pageSize"
          :total="pagination.total"
          :page-sizes="[10,20,50,100]"
          layout="total, sizes, prev, pager, next, jumper"
          @size-change="getApikeyData"
          @current-change="getApikeyData"
          class="mt-4"
        />
      </div>
  
      <!-- 确认操作弹窗（无下拉选择，仅展示机构名称） -->
      <el-dialog
        v-model="operateDialogVisible"
        :title="operateType === 'create' ? '生成机构ApiKey' : '重置机构ApiKey'"
        width="500px"
        :close-on-click-modal="false"
      >
        <div class="confirm-tip">
          <p><strong>机构名称：{{ currentRow?.company_name }}</strong></p>
          <p v-if="operateType === 'create'" class="tip-text">即将为该机构生成全新API密钥。</p>
          <p v-if="operateType === 'reset'" class="tip-text text-warning">重置后旧ApiKey、SecretKey将立即失效，业务鉴权会失败，请谨慎操作！</p>
        </div>
        <template #footer>
          <el-button @click="operateDialogVisible = false">取消</el-button>
          <el-button type="primary" :loading="submitLoading" @click="submitOperate">确认执行</el-button>
        </template>
      </el-dialog>
    </div>
  </template>
  
  <script setup>
  import { ref, reactive, onMounted } from "vue";
  import { ElMessage, ElMessageBox } from "element-plus";
  import { getapikeylist, setapikey, updateapikey } from "@/api/api";
  
  const loading = ref(false);
  const submitLoading = ref(false);
  
  const queryForm = reactive({
    companyName: ""
  });
  
  const pagination = reactive({
    page: 1,
    pageSize: 10,
    total: 0
  });
  
  const tableData = ref([]);
  
  //操作弹窗
  const operateDialogVisible = ref(false);
  const operateType = ref("create"); // create / reset
  const currentRow = ref(null);
  
  //获取apikey列表
  const getApikeyData = async () => {
    loading.value = true;
    try {
      const params = {
        page: pagination.page,
        pageSize: pagination.pageSize,
        companyName: queryForm.companyName || undefined
      };
      const res = await getapikeylist(params);
      if (res.code === 200) {
        tableData.value = res.data.list.map(item => ({
          ...item,
          showApi: false,
          showSecret: false
        }));
        pagination.total = res.data.total;
      }
    } catch (err) {
      ElMessage.error("获取ApiKey列表失败");
    } finally {
      loading.value = false;
    }
  };
  
  //脱敏字符串
  const maskStr = (str) => {
    if (!str) return "";
    if (str.length <= 12) return str;
    return str.slice(0, 8) + " ****** " + str.slice(-8);
  };
  
  //复制文本
  const copyText = async (text) => {
    try {
      await navigator.clipboard.writeText(text);
      ElMessage.success("复制成功");
    } catch {
      ElMessage.error("复制失败，请手动复制");
    }
  };
  
  //打开生成密钥弹窗
  const handleCreateKey = (row) => {
    operateType.value = "create";
    currentRow.value = row;
    operateDialogVisible.value = true;
  };
  
  //打开重置密钥弹窗
  const handleResetKey = (row) => {
    operateType.value = "reset";
    currentRow.value = row;
    operateDialogVisible.value = true;
  };
  
  //提交生成/重置
  const submitOperate = async () => {
    if (!currentRow.value?.company_id) {
      ElMessage.warning("缺少机构ID");
      return;
    }
    submitLoading.value = true;
    try {
      const payload = { company_id: currentRow.value.company_id };
      let res;
      if (operateType.value === "create") {
        res = await setapikey(payload);
      } else {
        res = await updateapikey(payload);
      }
      if (res.code === 200) {
        ElMessage.success(operateType.value === "create" ? "ApiKey生成成功" : "ApiKey重置成功");
        operateDialogVisible.value = false;
        getApikeyData();
      } else {
        ElMessage.error(res?.message || "操作失败");
      }
    } catch (e) {
      ElMessage.error("接口请求异常，操作失败");
    } finally {
      submitLoading.value = false;
    }
  };
  
  //删除密钥 todo 对接 deleteApikey(id)
  const handleDelete = (row) => {
    ElMessageBox.confirm(
      `确定要删除【${row.company_name}】的ApiKey？删除后该密钥立即失效，业务请求将无法通过鉴权！`,
      "风险提示",
      { confirmButtonText: "确认删除", cancelButtonText: "取消", type: "error" }
    ).then(async () => {
      // await deleteApikey(row.id)
      ElMessage.success("删除成功");
      getApikeyData();
    }).catch(() => {});
  };
  
  const handleReset = () => {
    queryForm.companyName = "";
    pagination.page = 1;
    getApikeyData();
  };
  
  onMounted(() => {
    getApikeyData();
  })
  </script>
  
  <style scoped>
  .apikey-manage-wrap {
  }
  .search-card {
    background: #fff;
    padding: 16px;
    border-radius: 6px;
    margin-bottom: 16px;
  }
  .table-card {
    background: #fff;
    padding: 16px;
    border-radius: 6px;
  }
  .mt-4 {
    margin-top: 16px;
  }
  .key-text {
    font-family: "Consolas", monospace;
    font-size: 12px;
    word-break: break-all;
  }
  .key-op {
    margin-top:4px;
  }
  .confirm-tip {
    padding:10px 0;
  }
  .tip-text {
    margin-top:10px;
    font-size:14px;
  }
  .text-warning {
    color:#e6a23c;
  }
  </style>